import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, type PartOutPlanData, type PartOutResultData } from '../api';
import { Button, Card, Checkbox, ErrorMessage, Field, Input, Notice, Select, cn, money, number, plural, unitMoney, useConfirm, useLeaveGuard } from '../ui';
import { ColorSwatch, ItemImage } from './ItemImage';

const today = () => new Date().toISOString().slice(0, 10);
const keyOf = (r: { itemType: string; itemNo: string; colorId: number }) => `${r.itemType}|${r.itemNo}|${r.colorId}`;

/**
 * Breaks sets into their parts. The parts list comes from BrickLink; each part is shown with its picture, the
 * quantity expected and a box for what was really there, which can differ. The sets leave stock when it is started
 * and the cost of those sets is shared across the parts; whatever is not counted yet can be counted later on the
 * part-out, a few at a time.
 */
export function PartOutCard({ lotId, setNo, onHand }: { lotId: number; setNo: string; onHand: number }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { confirm, dialog } = useConfirm();
  const [qty, setQty] = useState('1');
  const [extras, setExtras] = useState(false);
  const [breakMinifigs, setBreakMinifigs] = useState(false);
  const [method, setMethod] = useState<'by_value' | 'equal'>('by_value');
  const [partedOn, setPartedOn] = useState(today());
  const [actual, setActual] = useState<Record<string, string>>({});
  // Only the parts that are ticked are counted in. The rest stay on the part-out, to count another time.
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  const body = { qty: Number(qty), extras, breakMinifigs, method };
  const preview = useMutation({
    mutationFn: () => api<{ plan: PartOutPlanData }>(`/api/v1/lots/${lotId}/part-out/preview`, { body }).then((r) => r.plan),
  });
  const plan = preview.data;
  // Each part starts at the quantity expected, ready to be changed.
  useEffect(() => {
    if (plan) {
      setActual(Object.fromEntries(plan.rows.map((r) => [keyOf(r), String(r.qty)])));
      setTicked(new Set());
    }
  }, [plan]);

  const entered = (r: PartOutPlanData['rows'][number]) => {
    const n = Number(actual[keyOf(r)] ?? r.qty);
    return Number.isInteger(n) && n >= 0 ? n : NaN;
  };
  // What is counted in: the quantity entered for a ticked part, and nothing for one that is not ticked.
  const counted = (r: PartOutPlanData['rows'][number]) => (ticked.has(keyOf(r)) ? entered(r) : 0);
  const bad = plan?.rows.some((r) => ticked.has(keyOf(r)) && Number.isNaN(entered(r))) ?? false;
  const tickedCount = ticked.size;
  const countedTotal = plan ? plan.rows.reduce((s, r) => s + (Number.isNaN(counted(r)) ? 0 : counted(r)), 0) : 0;
  const differs = plan ? plan.rows.filter((r) => ticked.has(keyOf(r)) && !Number.isNaN(entered(r)) && entered(r) !== r.qty).length : 0;

  // Ticks and counts typed in are work worth keeping: leaving the page (a back swipe, say) asks first.
  const edited = plan ? plan.rows.some((r) => (actual[keyOf(r)] ?? String(r.qty)) !== String(r.qty)) : false;
  const guard = useLeaveGuard(tickedCount > 0 || edited, 'You have ticked or counted parts that are not saved yet. If you leave, you will have to tick and count them again.');

  const run = useMutation({
    mutationFn: (countNow: boolean) =>
      api<{ result: PartOutResultData }>(`/api/v1/lots/${lotId}/part-out`, {
        body: {
          ...body,
          partedOn,
          counted: plan!.rows.map((r) => ({ itemType: r.itemType, itemNo: r.itemNo, colorId: r.colorId, qty: countNow ? counted(r) : 0 })),
        },
      }).then((r) => r.result),
    onSuccess: (r) => {
      for (const k of ['lot', 'lots', 'dashboard', 'purchases', 'purchase', 'items', 'item-inventory', 'item-movements', 'item-profitability']) void qc.invalidateQueries({ queryKey: [k] });
      guard.release();
      navigate(`/purchases/${r.purchaseId}`);
    },
  });
  const valid = Number.isInteger(Number(qty)) && Number(qty) >= 1 && Number(qty) <= onHand;
  const reset = () => preview.reset();
  const shown = plan ? plan.rows.filter((r) => !filter.trim() || `${r.itemNo} ${r.name ?? ''} ${r.colorName ?? ''}`.toLowerCase().includes(filter.trim().toLowerCase())) : [];
  const tick = (r: PartOutPlanData['rows'][number], on: boolean) =>
    setTicked((t) => {
      const next = new Set(t);
      if (on) next.add(keyOf(r));
      else next.delete(keyOf(r));
      return next;
    });
  const tickShown = (on: boolean) => setTicked((t) => {
    const next = new Set(t);
    for (const r of shown) (on ? next.add(keyOf(r)) : next.delete(keyOf(r)));
    return next;
  });

  return (
    <Card title="Part out" className="min-w-0 lg:col-span-3">
      {dialog}
      {guard.dialog}
      <p className="mb-3 text-sm text-slate-600">
        Break {setNo} into its parts. The parts list comes from BrickLink (one read-only request each time). <strong>Tick the parts you have checked</strong> and enter what was really there; only those are counted in. The rest stay on the part-out to count another time, and the cost of the sets is shared across all the parts.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <Field label={`Sets to part out (of ${number(onHand)})`} className="w-40">
          <Input inputMode="numeric" value={qty} onChange={(e) => { setQty(e.target.value); reset(); }} />
        </Field>
        <Field label="Share the cost" className="w-64">
          <Select value={method} onChange={(e) => { setMethod(e.target.value as typeof method); reset(); }}>
            <option value="by_value">By value, using prices you have set</option>
            <option value="equal">Equally per piece</option>
          </Select>
        </Field>
        <Field label="Date" className="w-44">
          <Input type="date" value={partedOn} max={today()} onChange={(e) => setPartedOn(e.target.value)} />
        </Field>
        <div className="space-y-1.5 pb-1.5">
          <Checkbox label="Include the spare pieces BrickLink lists" checked={extras} onChange={(v) => { setExtras(v); reset(); }} />
          <Checkbox label="Break minifigures into parts too" checked={breakMinifigs} onChange={(v) => { setBreakMinifigs(v); reset(); }} />
        </div>
        <Button variant="secondary" disabled={!valid || preview.isPending} onClick={() => preview.mutate()}>
          {preview.isPending ? 'Asking BrickLink…' : 'Show the parts'}
        </Button>
      </div>
      <div className="mt-3 space-y-3">
        <ErrorMessage error={preview.error ?? run.error} />
        {plan && (
          <>
            <Notice tone={plan.warnings.length || plan.missingColours.length ? 'amber' : 'blue'}>
              {plural(plan.sets, 'set')} of {plan.setNo} cost {money(plan.cost)} and should make {plural(plan.pieces, 'piece')} in {plural(plan.rows.length, 'part')} ({plan.condition === 'N' ? 'new' : 'used'}). Starting takes the sets out of stock.
              {plan.warnings.map((w) => (
                <span key={w} className="block text-xs">
                  {w}
                </span>
              ))}
              {plan.missingColours.length > 0 && (
                <span className="block text-xs">
                  {plural(plan.missingColours.length, 'colour')} in this set {plan.missingColours.length === 1 ? 'is' : 'are'} not in the colour table, so this cannot be done yet. Load the colours and categories from Channels first.
                </span>
              )}
            </Notice>

            <div className="flex flex-wrap items-center gap-3">
              <Input aria-label="Filter the parts" className="w-60" placeholder="Filter parts" value={filter} onChange={(e) => setFilter(e.target.value)} />
              <Button small variant="ghost" onClick={() => tickShown(true)}>
                Tick all shown
              </Button>
              <Button small variant="ghost" disabled={tickedCount === 0} onClick={() => tickShown(false)}>
                Untick all shown
              </Button>
              <span className="ml-auto text-sm text-slate-600">
                {number(tickedCount)} of {plural(plan.rows.length, 'part')} ticked · counting {number(countedTotal)} of {number(plan.pieces)} pieces
                {differs > 0 && <span className="text-amber-700"> · {plural(differs, 'part')} differ{differs === 1 ? 's' : ''}</span>}
              </span>
            </div>

            <div className="max-h-[32rem] overflow-auto rounded-lg border border-slate-200">
              <table className="data w-full min-w-[48rem] text-sm">
                <thead className="sticky top-0 z-10 bg-white">
                  <tr>
                    <th className="w-10">
                      <span className="sr-only">Tick</span>
                    </th>
                    <th className="min-w-14" />
                    <th>Part</th>
                    <th>Colour</th>
                    <th className="text-right">Expected</th>
                    <th className="text-right">Actual</th>
                    <th className="text-right">Cost each</th>
                    <th className="text-right">Priced at</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const n = entered(r);
                    const on = ticked.has(keyOf(r));
                    return (
                      <tr key={keyOf(r)} className={on ? 'bg-emerald-50/50' : undefined}>
                        <td className="w-10">
                          <input type="checkbox" aria-label={`Count in ${r.itemNo} ${r.colorName ?? ''}`} className="size-4 rounded border-slate-300" checked={on} onChange={(e) => tick(r, e.target.checked)} />
                        </td>
                        <td className="min-w-14">
                          <ItemImage itemType={r.itemType} itemNo={r.itemNo} colorId={r.colorId} />
                        </td>
                        <td>
                          <span className="font-medium">{r.itemNo}</span>
                          <div className="max-w-72 truncate text-xs text-slate-500">
                            {r.name} {!r.itemExists && <span className="text-amber-700">· new to your catalogue</span>}
                          </div>
                        </td>
                        <td className="whitespace-nowrap">
                          <ColorSwatch rgb={r.colorRgb} name={r.colorName ?? `colour ${r.colorId}`} />
                        </td>
                        <td className="text-right tabular-nums">{number(r.qty)}</td>
                        <td className="text-right">
                          <Input
                            aria-label={`Actual: ${r.itemNo} ${r.colorName ?? ''}`}
                            className={cn('w-24 text-right', !on && 'opacity-50', on && Number.isNaN(n) && 'border-red-400', on && !Number.isNaN(n) && n !== r.qty && 'border-amber-400 bg-amber-50')}
                            inputMode="numeric"
                            onFocus={() => tick(r, true)}
                            value={actual[keyOf(r)] ?? String(r.qty)}
                            onChange={(e) => setActual({ ...actual, [keyOf(r)]: e.target.value })}
                          />
                        </td>
                        <td className="text-right tabular-nums">{unitMoney(r.unitCost)}</td>
                        <td className="text-right tabular-nums">{r.knownPrice ? unitMoney(r.knownPrice) : <span className="text-slate-400">not set</span>}</td>
                      </tr>
                    );
                  })}
                  {shown.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-4 text-center text-slate-500">
                        No parts match.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {bad && <Notice tone="amber">Every ticked part needs a whole number, zero or more.</Notice>}
            {tickedCount === 0 && <p className="text-sm text-slate-500">Nothing is ticked, so nothing would be counted in yet. Tick the parts you have checked, or start the part-out and count later.</p>}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                disabled={run.isPending || bad || tickedCount === 0 || plan.missingColours.length > 0}
                onClick={async () => (await confirm(`Part out ${plural(plan.sets, 'set')} of ${plan.setNo} and count in ${plural(tickedCount, 'ticked part')} (${plural(countedTotal, 'piece')}), dated ${partedOn}? The sets leave stock now, and the other parts can be counted later.`, { label: 'Part out' })) && run.mutate(true)}
              >
                {run.isPending ? 'Parting out…' : `Part out and count in ${plural(tickedCount, 'part')}`}
              </Button>
              <Button variant="secondary" disabled={run.isPending || plan.missingColours.length > 0} onClick={async () => (await confirm(`Take ${plural(plan.sets, 'set')} of ${plan.setNo} out of stock now and count the parts in later?`, { label: 'Start the part-out' })) && run.mutate(false)}>
                Start now, count later
              </Button>
              <span className="text-xs text-slate-500">
                Either way you finish on the part-out, where <Link to="/purchases" className="underline">Purchases</Link> lists it.
              </span>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
